import React, { useState } from "react";
import ToggleSlider from "./base/ToggleSlider.jsx";
import Button from "./base/Button.jsx";
import { theme } from "../theme.js";
import { TrashIcon, PencilSquareIcon, PlusIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { icon } from "leaflet";

export default function OptionItem({ option, onUpdate, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({...option, description: option.description || ""});
  const [choices, setChoices] = useState(option.choices || []);
  const [newChoice, setNewChoice] = useState("");
  const [isYesNo, setIsYesNo] = useState(option.type === "boolean");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData((prev) => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value 
    }));
  };

  const handleAddChoice = () => {
    if (newChoice.trim()) {
      setChoices([...choices, newChoice.trim()]);
      setNewChoice("");
      setError(""); // Clear error when adding a choice
    }
  };

  const handleRemoveChoice = (index) => {
    setChoices(choices.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    // Validate: if not YES/NO, must have at least 2 choices
    if (!isYesNo && choices.length < 2) {
      setError("Veuillez ajouter au moins 2 options disponibles ou basculer en mode OUI/NON.");
      return;
    }
    
    const updatedOption = { 
      ...editData,
      type: isYesNo ? "boolean" : "enum",
      choices: !isYesNo && Array.isArray(choices) ? choices : [] 
    };
    onUpdate(updatedOption);
    setIsEditing(false);
    setError("");
  };

  if (!isEditing) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <p style={styles.title}>{option.name}</p>
          <p style={styles.subtitle}>
            {option.required ? "✓ Obligatoire" : "○ Optionnel"} • <span style={{ whiteSpace: "nowrap" }}>{option.price}€</span>
          </p>
        </div>
        <div style={styles.actions}>
          <button onClick={() => onDelete(option.key)} style={styles.deleteBtn}>
            <TrashIcon style={styles.icon} />
          </button>
          <button onClick={() => setIsEditing(true)} style={styles.editBtn}>
            <PencilSquareIcon style={styles.icon} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <h3 style={styles.modalTitle}>Éditer l'option</h3>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Nom de l'option</label>
          <input
            type="text"
            name="name"
            value={editData.name}
            onChange={handleChange}
            style={styles.input}
            placeholder="Ex: T-shirt"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Prix (€)</label>
          <input
            type="number"
            name="price"
            value={editData.price}
            onChange={handleChange}
            style={styles.input}
            placeholder="0"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Description</label>
          <input
            type="text"
            name="description"
            value={editData.description}
            onChange={handleChange}
            style={styles.input}
            placeholder="Décrivez cette option..."
          />
        </div>

        <div style={styles.formGroup}>
          <ToggleSlider
            checked={isYesNo}
            onChange={() => {
              setIsYesNo(!isYesNo);
              setError(""); // Clear error when toggling YES/NO
              if (!isYesNo) {
                setChoices([]);
              }
            }}
            label="Choix OUI/NON ?"
            size="medium"
          />
        </div>

        {!isYesNo && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Options disponibles</label>
            <div style={styles.choicesContainer}>
              {choices.map((choice, index) => (
                <div key={index} style={styles.choiceTag}>
                  <span>{choice}</span>
                  <button
                    onClick={() => handleRemoveChoice(index)}
                    style={styles.removeChoiceBtn}
                  >
                    <TrashIcon style={styles.icon} />
                  </button>
                </div>
              ))}
            </div>
            
            <div style={styles.addChoiceContainer}>
              <input
                type="text"
                value={newChoice}
                onChange={(e) => setNewChoice(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddChoice()}
                style={styles.choiceInput}
                placeholder="Ajouter une option..."
              />
              <button onClick={handleAddChoice} style={styles.addChoiceBtn}>
                <PlusIcon style={{...styles.icon, color: theme.colors.primary}} />
              </button>
            </div>
          </div>
        )}

        {isYesNo && (
          <div style={{...styles.formGroup, ...styles.disabledSection}}>
            <label style={{...styles.label, color: "#999"}}>Options disponibles</label>
            <div style={styles.choicesContainer}>
              {choices.map((choice, index) => (
                <div key={index} style={{...styles.choiceTag, ...styles.disabledChoice}}>
                  <span>{choice}</span>
                  <button
                    onClick={() => handleRemoveChoice(index)}
                    style={{...styles.removeChoiceBtn, opacity: 0.5, cursor: "not-allowed"}}
                    disabled
                  >
                    <XMarkIcon style={styles.icon} />
                  </button>
                </div>
              ))}
            </div>
            
            <div style={styles.addChoiceContainer}>
              <input
                type="text"
                value={newChoice}
                onChange={(e) => setNewChoice(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddChoice()}
                style={{...styles.choiceInput, ...styles.disabledInput}}
                placeholder="Ajouter une option..."
                disabled
              />
              <button 
                onClick={handleAddChoice} 
                style={{...styles.addChoiceBtn, ...styles.disabledButton}}
                disabled
              >
                <PlusIcon style={styles.icon} />
              </button>
            </div>
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="required"
              checked={editData.required || false}
              onChange={handleChange}
              style={styles.checkbox}
            />
            Obligatoire pour tous les participants
          </label>
        </div>

        {error && (
          <div style={styles.errorBox}>
            <p style={styles.errorText}>{error}</p>
          </div>
        )}

        <div style={styles.editActions}>
          <Button icon={CheckIcon} title="Enregistrer" onClick={handleSave} style="dark" />
          <Button icon={XMarkIcon} title="Annuler" onClick={() => setIsEditing(false)} style="cancel" />
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.radius.lg,
    marginBottom: theme.spacing.sm,
    border: `1px solid ${theme.colors.gray200}`,
  },
  content: {
    flex: 1,
  },
  title: {
    margin: `0 0 ${theme.spacing.sm} 0`,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.gray800,
    fontSize: theme.fonts.size.base,
  },
  subtitle: {
    margin: "0",
    fontSize: theme.fonts.size.base,
    color: theme.colors.gray500,
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
    marginLeft: theme.spacing.lg,
  },
  editBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: theme.iconSize.sm,
    padding: `${theme.spacing.xs} ${theme.spacing.xs}`,
  },
  deleteBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: theme.iconSize.sm,
    padding: `${theme.spacing.xs} ${theme.spacing.xs}`,
  },
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: theme.zIndex.modal,
  },
  modalContent: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.radius.xl,
    padding: theme.spacing.huge,
    boxShadow: theme.shadows.xl,
    maxWidth: "500px",
    width: "90%",
    maxHeight: "80vh",
    overflowY: "auto",
  },
  modalTitle: {
    margin: `0 0 ${theme.spacing.xxl} 0`,
    fontSize: theme.fonts.size.lg,
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.gray800,
  },
  formGroup: {
    marginBottom: theme.spacing.lg,
  },
  label: {
    display: "block",
    marginBottom: theme.spacing.sm,
    fontSize: theme.fonts.size.base,
    fontWeight: theme.fonts.weight.medium,
    color: theme.colors.gray700,
  },
  input: {
    width: "100%",
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    border: `1px solid ${theme.colors.gray300}`,
    borderRadius: theme.radius.md,
    fontSize: theme.fonts.size.base,
    boxSizing: "border-box",
    fontFamily: theme.fonts.family.primary,
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    fontSize: theme.fonts.size.base,
    color: theme.colors.gray700,
    cursor: "pointer",
  },
  checkbox: {
    cursor: "pointer",
    width: "16px",
    height: "16px",
    accentColor: theme.colors.dark,
    borderRadius: theme.radius.md,
  },
  choicesContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
    minHeight: "32px",
  },
  choiceTag: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.infoBg,
    color: theme.colors.textInverted,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.md,
    fontSize: theme.fonts.size.sm,
  },
  removeChoiceBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: theme.colors.bgDark,
    fontSize: theme.fonts.size.base,
    padding: "0",
  },
  addChoiceContainer: {
    display: "flex",
    gap: theme.spacing.sm,
  },
  choiceInput: {
    flex: 1,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    border: `1px solid ${theme.colors.gray300}`,
    borderRadius: theme.radius.md,
    fontSize: theme.fonts.size.base,
    fontFamily: theme.fonts.family.primary,
  },
  addChoiceBtn: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.light,
    color: theme.colors.textInverted,
    border: "none",
    borderRadius: theme.radius.md,
    cursor: "pointer",
  },
  editActions: {
    display: "flex",
    gap: theme.spacing.sm,
    marginTop: theme.spacing.xxl,
  },
  saveBtn: {
    flex: 1,
    padding: `${theme.spacing.md} ${theme.spacing.md}`,
    backgroundColor: theme.colors.light,
    color: theme.colors.textInverted,
    border: "none",
    borderRadius: theme.radius.md,
    cursor: "pointer",
    fontWeight: theme.fonts.weight.medium,
    fontFamily: theme.fonts.family.primary,
  },
  cancelBtn: {
    flex: 1,
    padding: `${theme.spacing.md} ${theme.spacing.md}`,
    backgroundColor: theme.colors.error,
    color: theme.colors.bgLight,
    border: "none",
    borderRadius: theme.radius.md,
    cursor: "pointer",
    fontWeight: theme.fonts.weight.medium,
    fontFamily: theme.fonts.family.primary,
  },
  disabledSection: {
    opacity: 0.6,
    backgroundColor: theme.colors.gray100,
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
    pointerEvents: "none",
  },
  disabledChoice: {
    backgroundColor: theme.colors.gray200,
    color: theme.colors.gray500,
  },
  disabledInput: {
    backgroundColor: theme.colors.gray100,
    color: theme.colors.gray500,
    cursor: "not-allowed",
    opacity: 0.6,
  },
  disabledButton: {
    backgroundColor: theme.colors.gray300,
    cursor: "not-allowed",
    opacity: 0.6,
  },
  errorBox: {
    backgroundColor: theme.colors.errorBg,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  errorText: {
    margin: "0",
    color: theme.colors.error,
    fontSize: theme.fonts.size.base,
    fontWeight: theme.fonts.weight.medium,
  },
  icon: {
    width: theme.iconSize.sm,
    height: theme.iconSize.sm,
    color: theme.colors.gray500,
    verticalAlign: "middle",
  },
};
